import { Component, type ErrorInfo, type ReactNode } from 'react'

import { reportCrash } from './crash'

/**
 * Root React error boundary. Catches errors thrown during render / lifecycle
 * and routes them to the analytics crash reporter, then falls back to a
 * minimal recovery UI. Mount this at the AppShell root so any component
 * subtree can crash without taking the whole app down.
 *
 * ### Why a class component
 *   `componentDidCatch` is only available on class components; there's no
 *   hook equivalent. Rest of the app is functional.
 *
 * ### Not for network / async errors
 *   TanStack Query errors, promise rejections in event handlers, and
 *   `window.onerror`-level failures are handled by `installGlobalCrashHandlers`
 *   in `crash.ts` — this boundary only catches synchronous render errors.
 *
 * ### Fallback UI
 *   Deliberately minimal — a full-featured "something went wrong" screen
 *   with retry logic belongs at a higher level (per-route boundaries). This
 *   is the last-line-of-defence catch that keeps the user from staring at
 *   a white screen.
 */
export class AnalyticsErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean }
> {
  override state = { hasError: false }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportCrash(error, {
      source: 'boundary',
      componentStack: info.componentStack ?? undefined,
    })
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="font-display text-lg font-bold text-fg">
            Something broke here.
          </p>
          <p className="max-w-md text-sm text-fg-muted">
            We've logged the error and are looking at it. Refresh the page to
            try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 rounded-md border border-border bg-surface px-4 py-2 text-sm font-semibold text-fg hover:bg-surface-strong"
          >
            Refresh
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
